import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Typography,
  Button,
  Tabs,
  Tag,
  Space,
  Spin,
  Alert,
  Badge,
  Tooltip,
  Input,
  message,
  Empty,
  Pagination,
  Select,
  Card,
  Modal,
  Form,
  Popconfirm,
  Drawer,
} from 'antd';
import {
  ReloadOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  SearchOutlined,
  ExportOutlined,
  GoogleOutlined,
  ArrowRightOutlined,
  SwapOutlined,
  DoubleRightOutlined,
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  SaveOutlined,
} from '@ant-design/icons';
import { colors, spacing, radius, typography, shadows } from '../../styles/monochromeTheme';
import { readSheetData, getSheetInfo, updateSheetRow, clearSheetRow, appendSheetRow } from '../../api/googleSheetsExport';
import { isGoogleAuthenticated, getGoogleAuthUrl } from '../../api/googleDrive';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';

dayjs.locale('fr');

const { Title, Text } = Typography;

// Google Sheet ID from the URL
const CLAIRANCES_SHEET_ID = '1jcRgn-HVpFlejSZL2JRiLps_M0HfCqcVyznrpJrsGK4';

// Default sheet names (fallback if API fails)
const DEFAULT_SHEET_NAMES = ['SXM', 'SBH'];

// Items per page
const PAGE_SIZE_OPTIONS = [10, 20, 50];
const DEFAULT_PAGE_SIZE = 15;

const styles = {
  page: {
    minHeight: '100vh',
    background: colors.bgPrimary,
    padding: spacing.xl,
  },
  container: {
    maxWidth: '1400px',
    margin: '0 auto',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: spacing.xl,
    paddingBottom: spacing.lg,
    borderBottom: `1px solid ${colors.borderLight}`,
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing.md,
  },
  headerIcon: {
    width: '56px',
    height: '56px',
    borderRadius: radius.xl,
    background: 'linear-gradient(135deg, #1890ff, #096dd9)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '28px',
  },
  card: {
    background: colors.bgSecondary,
    borderRadius: radius.xl,
    border: `1px solid ${colors.borderLight}`,
    boxShadow: shadows.md,
    padding: spacing.lg,
    marginBottom: spacing.lg,
  },
  tableContainer: {
    overflowX: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'separate',
    borderSpacing: 0,
  },
  th: {
    padding: `${spacing.sm} ${spacing.md}`,
    textAlign: 'left',
    fontWeight: typography.fontWeight.semibold,
    fontSize: typography.fontSize.xs,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    color: colors.textMuted,
    borderBottom: `2px solid ${colors.borderMedium}`,
    background: colors.bgTertiary,
    position: 'sticky',
    top: 0,
  },
  td: {
    padding: `${spacing.sm} ${spacing.md}`,
    borderBottom: `1px solid ${colors.borderLight}`,
    fontSize: typography.fontSize.sm,
    verticalAlign: 'middle',
  },
  statsCard: {
    display: 'flex',
    gap: spacing.lg,
    marginBottom: spacing.lg,
    flexWrap: 'wrap',
  },
  statItem: {
    background: colors.bgSecondary,
    borderRadius: radius.lg,
    padding: spacing.md,
    flex: '1 1 120px',
    border: `1px solid ${colors.borderLight}`,
    textAlign: 'center',
    minWidth: 100,
  },
  legendContainer: {
    display: 'flex',
    gap: spacing.lg,
    marginBottom: spacing.md,
    flexWrap: 'wrap',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing.xs,
    fontSize: typography.fontSize.sm,
  },
  workflowCard: {
    background: 'linear-gradient(135deg, #f6f9fc, #eef2f7)',
    borderRadius: radius.lg,
    padding: spacing.md,
    marginBottom: spacing.lg,
    border: `1px solid ${colors.borderLight}`,
  },
  workflowItem: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing.sm,
    fontSize: typography.fontSize.sm,
    color: colors.textSecondary,
  },
  paginationContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: spacing.lg,
    paddingTop: spacing.md,
    borderTop: `1px solid ${colors.borderLight}`,
  },
};

// Parse date from various formats
const parseDate = (dateStr) => {
  if (!dateStr) return null;

  // Try DD/MM/YYYY format
  const parts = dateStr.split('/');
  if (parts.length === 3) {
    const [day, month, year] = parts;
    const fullYear = year.length === 2 ? `20${year}` : year;
    return dayjs(`${fullYear}-${month}-${day}`);
  }

  return dayjs(dateStr);
};

// Check if clearance is expiring (within 48h)
const isExpiring = (dateStr) => {
  const date = parseDate(dateStr);
  if (!date || !date.isValid()) return false;

  const now = dayjs();
  const hoursDiff = date.diff(now, 'hour');

  return hoursDiff <= 48 && hoursDiff >= -24; // Within 48h future or 24h past
};

// Check if clearance is pending (not validated)
const isPending = (validStr) => {
  return !validStr || validStr.trim() !== '✔️';
};

// Parse clearance row for ENTREES
const parseEntreeRow = (row, rowIndex) => {
  if (!row || row.length < 6) return null;

  return {
    id: `entree-${rowIndex}`,
    numero: row[0] || '',
    bateau: row[1] || '',
    nomPax: row[2] || '',
    date: row[3] || '',
    heure: row[4] || '',
    valide: row[5] || '',
    rowIndex,
  };
};

// Parse clearance row for SORTIES
const parseSortieRow = (row, rowIndex) => {
  if (!row || row.length < 12) return null;

  return {
    id: `sortie-${rowIndex}`,
    numero: row[6] || '',
    bateau: row[7] || '',
    date: row[8] || '',
    nomPax: row[9] || '',
    heure: row[10] || '',
    valide: row[11] || '',
    rowIndex,
  };
};

function ClairanceTracker() {
  const [loading, setLoading] = useState(true);
  const [dataEntrees, setDataEntrees] = useState([]);
  const [dataSorties, setDataSorties] = useState([]);
  const [activeTab, setActiveTab] = useState(null);
  const [sheetNames, setSheetNames] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);

  // Store data for both territories
  const [sbhData, setSbhData] = useState({ entrees: [], sorties: [] });
  const [sxmData, setSxmData] = useState({ entrees: [], sorties: [] });

  // Pagination states
  const [currentPageEntrees, setCurrentPageEntrees] = useState(1);
  const [currentPageSorties, setCurrentPageSorties] = useState(1);
  const [currentPagePaired, setCurrentPagePaired] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  // View mode: 'separate', 'combined', or 'paired'
  const [viewMode, setViewMode] = useState('paired');

  // Edit/Add modal states
  const [editDrawerVisible, setEditDrawerVisible] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editForm] = Form.useForm();
  const [saving, setSaving] = useState(false);

  // Add modal state
  const [addModalVisible, setAddModalVisible] = useState(false);
  const [addForm] = Form.useForm();
  const [addType, setAddType] = useState('entree'); // 'entree' or 'sortie'
  const [addTerritory, setAddTerritory] = useState('SBH');

  const checkAuth = useCallback(() => {
    setIsAuthenticated(isGoogleAuthenticated());
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Fetch sheet names first
  const fetchSheetNames = useCallback(async () => {
    if (!isAuthenticated) return;

    try {
      const info = await getSheetInfo(CLAIRANCES_SHEET_ID);
      const sheets = info.sheets?.map(s => s.properties.title) || [];
      console.log('Available sheets:', sheets);
      setSheetNames(sheets);

      if (sheets.length > 0 && !activeTab) {
        setActiveTab(sheets[0]);
      }
    } catch (err) {
      console.error('Error fetching sheet names:', err);
      console.log('Using default sheet names as fallback:', DEFAULT_SHEET_NAMES);
      setSheetNames(DEFAULT_SHEET_NAMES);
      if (!activeTab) {
        setActiveTab(DEFAULT_SHEET_NAMES[0]);
      }
      message.warning('Utilisation des onglets par défaut (SXM, SBH)');
    }
  }, [isAuthenticated, activeTab]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchSheetNames();
    }
  }, [isAuthenticated, fetchSheetNames]);

  // Helper to sort by date descending
  const sortByDate = (a, b) => {
    const dateA = parseDate(a.date);
    const dateB = parseDate(b.date);
    if (!dateA && !dateB) return 0;
    if (!dateA) return 1;
    if (!dateB) return -1;
    return dateB.valueOf() - dateA.valueOf();
  };

  // Fetch data from a specific sheet
  const fetchSheetData = async (sheetName) => {
    const rawData = await readSheetData(CLAIRANCES_SHEET_ID, `${sheetName}!A:L`);
    const dataRows = rawData.slice(2);

    const entrees = dataRows
      .map((row, idx) => parseEntreeRow(row, idx))
      .filter(item => item && (item.numero || item.bateau || item.date))
      .map(item => ({ ...item, territory: sheetName }));

    const sorties = dataRows
      .map((row, idx) => parseSortieRow(row, idx))
      .filter(item => item && (item.numero || item.bateau || item.date))
      .map(item => ({ ...item, territory: sheetName }));

    return { entrees, sorties };
  };

  const fetchData = useCallback(async () => {
    if (!isAuthenticated) return;

    setLoading(true);
    setError(null);

    try {
      console.log('Fetching data from both SBH and SXM sheets...');

      // Fetch both territories in parallel
      const [sbhResult, sxmResult] = await Promise.all([
        fetchSheetData('SBH').catch(err => {
          console.warn('Error fetching SBH:', err);
          return { entrees: [], sorties: [] };
        }),
        fetchSheetData('SXM').catch(err => {
          console.warn('Error fetching SXM:', err);
          return { entrees: [], sorties: [] };
        }),
      ]);

      // Store data by territory
      setSbhData({
        entrees: sbhResult.entrees.sort(sortByDate),
        sorties: sbhResult.sorties.sort(sortByDate),
      });
      setSxmData({
        entrees: sxmResult.entrees.sort(sortByDate),
        sorties: sxmResult.sorties.sort(sortByDate),
      });

      // Set initial data based on active tab or default to SBH
      const initialTab = activeTab || 'SBH';
      if (initialTab === 'SBH') {
        setDataEntrees(sbhResult.entrees.sort(sortByDate));
        setDataSorties(sbhResult.sorties.sort(sortByDate));
      } else {
        setDataEntrees(sxmResult.entrees.sort(sortByDate));
        setDataSorties(sxmResult.sorties.sort(sortByDate));
      }

      setLastRefresh(dayjs());

      // Reset pagination when data changes
      setCurrentPageEntrees(1);
      setCurrentPageSorties(1);
      setCurrentPagePaired(1);

    } catch (err) {
      console.error('Error fetching clairances:', err);
      setError(err.message);
      message.error('Erreur lors du chargement des clairances');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchData();
    }
  }, [fetchData, isAuthenticated]);

  // Update displayed data when activeTab changes (for combined/separate views)
  useEffect(() => {
    if (activeTab === 'SBH') {
      setDataEntrees(sbhData.entrees);
      setDataSorties(sbhData.sorties);
    } else if (activeTab === 'SXM') {
      setDataEntrees(sxmData.entrees);
      setDataSorties(sxmData.sorties);
    }
  }, [activeTab, sbhData, sxmData]);

  const handleAuth = () => {
    try {
      const authUrl = getGoogleAuthUrl('/clairance-tracker');
      window.location.href = authUrl;
    } catch (err) {
      message.error('Erreur lors de l\'authentification Google');
    }
  };

  // Open edit drawer
  const handleEdit = (item, type, territory) => {
    setEditingItem({ ...item, type, territory });
    editForm.setFieldsValue({
      numero: item.numero || '',
      bateau: item.bateau || '',
      nomPax: item.nomPax || '',
      date: item.date || '',
      heure: item.heure || '',
      valide: item.valide === '✔️',
    });
    setEditDrawerVisible(true);
  };

  // Save edited item
  const handleSaveEdit = async () => {
    try {
      const values = await editForm.validateFields();
      setSaving(true);

      const { type, territory, rowIndex } = editingItem;

      // Calculate row number (add 3 because rows start at 1, and we skip 2 header rows)
      const actualRow = rowIndex + 3;

      // Determine column range based on type
      // ENTREES: columns A-F (1-6)
      // SORTIES: columns G-L (7-12)
      const startCol = type === 'entree' ? 'A' : 'G';
      const endCol = type === 'entree' ? 'F' : 'L';
      const range = `${territory}!${startCol}${actualRow}:${endCol}${actualRow}`;

      // Build row values based on type
      let rowValues;
      if (type === 'entree') {
        // ENTREES: numero, bateau, nomPax, date, heure, valide
        rowValues = [
          values.numero || '',
          values.bateau || '',
          values.nomPax || '',
          values.date || '',
          values.heure || '',
          values.valide ? '✔️' : '',
        ];
      } else {
        // SORTIES: numero, bateau, date, nomPax, heure, valide
        rowValues = [
          values.numero || '',
          values.bateau || '',
          values.date || '',
          values.nomPax || '',
          values.heure || '',
          values.valide ? '✔️' : '',
        ];
      }

      console.log('Updating row:', range, rowValues);
      await updateSheetRow(CLAIRANCES_SHEET_ID, range, rowValues);

      message.success('Clairance mise à jour');
      setEditDrawerVisible(false);
      setEditingItem(null);
      editForm.resetFields();

      // Refresh data
      fetchData();
    } catch (err) {
      console.error('Error saving edit:', err);
      message.error('Erreur lors de la sauvegarde: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  // Delete item
  const handleDelete = async (item, type, territory) => {
    try {
      const actualRow = item.rowIndex + 3;
      const startCol = type === 'entree' ? 'A' : 'G';
      const endCol = type === 'entree' ? 'F' : 'L';
      const range = `${territory}!${startCol}${actualRow}:${endCol}${actualRow}`;

      console.log('Clearing row:', range);
      await clearSheetRow(CLAIRANCES_SHEET_ID, range);

      message.success('Clairance supprimée');
      fetchData();
    } catch (err) {
      console.error('Error deleting:', err);
      message.error('Erreur lors de la suppression: ' + err.message);
    }
  };

  // Open add modal
  const handleOpenAdd = () => {
    setAddModalVisible(true);
    addForm.resetFields();
    addForm.setFieldsValue({
      date: dayjs().format('DD/MM/YYYY'),
    });
  };

  // Add new item
  const handleAdd = async () => {
    try {
      const values = await addForm.validateFields();
      setSaving(true);

      // Determine which sheet and column range
      const range = `${addTerritory}!A:L`;

      // Build row with 12 columns (A-L)
      // ENTREES: A-F, SORTIES: G-L
      let rowValues;
      if (addType === 'entree') {
        rowValues = [
          values.numero || '',
          values.bateau || '',
          values.nomPax || '',
          values.date || '',
          values.heure || '',
          values.valide ? '✔️' : '',
          '', '', '', '', '', '' // Empty sortie columns
        ];
      } else {
        rowValues = [
          '', '', '', '', '', '', // Empty entree columns
          values.numero || '',
          values.bateau || '',
          values.date || '',
          values.nomPax || '',
          values.heure || '',
          values.valide ? '✔️' : '',
        ];
      }

      console.log('Appending row to:', addTerritory, rowValues);
      await appendSheetRow(CLAIRANCES_SHEET_ID, range, rowValues);

      message.success('Clairance ajoutée');
      setAddModalVisible(false);
      addForm.resetFields();

      // Refresh data
      fetchData();
    } catch (err) {
      console.error('Error adding:', err);
      message.error('Erreur lors de l\'ajout: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  // Toggle validation status quickly
  const handleToggleValidation = async (item, type, territory) => {
    try {
      const actualRow = item.rowIndex + 3;
      // Validation column: F for entrees, L for sorties
      const col = type === 'entree' ? 'F' : 'L';
      const range = `${territory}!${col}${actualRow}`;

      const newValue = item.valide === '✔️' ? '' : '✔️';

      console.log('Toggling validation:', range, newValue);
      await updateSheetRow(CLAIRANCES_SHEET_ID, range, [newValue]);

      message.success(newValue ? 'Clairance validée' : 'Validation retirée');
      fetchData();
    } catch (err) {
      console.error('Error toggling validation:', err);
      message.error('Erreur: ' + err.message);
    }
  };

  const filterData = (data) => {
    if (!searchQuery) return data;
    const query = searchQuery.toLowerCase();
    return data.filter(item =>
      item.numero?.toLowerCase().includes(query) ||
      item.bateau?.toLowerCase().includes(query) ||
      item.nomPax?.toLowerCase().includes(query) ||
      item.date?.toLowerCase().includes(query)
    );
  };

  const getStats = (entrees, sorties) => {
    const totalEntrees = entrees.length;
    const totalSorties = sorties.length;
    const pendingEntrees = entrees.filter(item => isPending(item.valide)).length;
    const pendingSorties = sorties.filter(item => isPending(item.valide)).length;
    const expiringEntrees = entrees.filter(item => isExpiring(item.date) && isPending(item.valide)).length;
    const expiringSorties = sorties.filter(item => isExpiring(item.date) && isPending(item.valide)).length;

    return {
      totalEntrees,
      totalSorties,
      pendingEntrees,
      pendingSorties,
      expiringEntrees,
      expiringSorties,
      total: totalEntrees + totalSorties,
      pending: pendingEntrees + pendingSorties,
      expiring: expiringEntrees + expiringSorties,
    };
  };

  // Paginate data
  const paginateData = (data, currentPage) => {
    const startIndex = (currentPage - 1) * pageSize;
    return data.slice(startIndex, startIndex + pageSize);
  };

  // Create paired view - match SBH sorties with SXM entrees and vice versa
  const pairedClairances = useMemo(() => {
    // Normalize boat name for matching
    const normalizeBoat = (name) => {
      if (!name) return '';
      return name.toLowerCase().trim().replace(/\s+/g, ' ');
    };

    // Parse date for comparison
    const getDateKey = (dateStr) => {
      const d = parseDate(dateStr);
      return d && d.isValid() ? d.format('YYYY-MM-DD') : null;
    };

    // Create all entries with their type and source
    const allEntries = [
      ...sbhData.sorties.map(s => ({ ...s, type: 'sortie', territory: 'SBH', key: `sbh-sortie-${s.rowIndex}` })),
      ...sbhData.entrees.map(e => ({ ...e, type: 'entree', territory: 'SBH', key: `sbh-entree-${e.rowIndex}` })),
      ...sxmData.sorties.map(s => ({ ...s, type: 'sortie', territory: 'SXM', key: `sxm-sortie-${s.rowIndex}` })),
      ...sxmData.entrees.map(e => ({ ...e, type: 'entree', territory: 'SXM', key: `sxm-entree-${e.rowIndex}` })),
    ];

    // Group by boat and date for matching
    const groups = {};
    allEntries.forEach(entry => {
      const boatKey = normalizeBoat(entry.bateau);
      const dateKey = getDateKey(entry.date);
      if (!boatKey || !dateKey) return;

      const groupKey = `${boatKey}-${dateKey}`;
      if (!groups[groupKey]) {
        groups[groupKey] = {
          boat: entry.bateau,
          date: entry.date,
          dateObj: parseDate(entry.date),
          sbhSortie: null,
          sbhEntree: null,
          sxmSortie: null,
          sxmEntree: null,
        };
      }

      if (entry.territory === 'SBH' && entry.type === 'sortie') {
        groups[groupKey].sbhSortie = entry;
      } else if (entry.territory === 'SBH' && entry.type === 'entree') {
        groups[groupKey].sbhEntree = entry;
      } else if (entry.territory === 'SXM' && entry.type === 'sortie') {
        groups[groupKey].sxmSortie = entry;
      } else if (entry.territory === 'SXM' && entry.type === 'entree') {
        groups[groupKey].sxmEntree = entry;
      }
    });

    // Convert to array and sort by date descending
    return Object.values(groups)
      .filter(g => g.sbhSortie || g.sbhEntree || g.sxmSortie || g.sxmEntree)
      .sort((a, b) => {
        if (!a.dateObj && !b.dateObj) return 0;
        if (!a.dateObj) return 1;
        if (!b.dateObj) return -1;
        return b.dateObj.valueOf() - a.dateObj.valueOf();
      });
  }, [sbhData, sxmData]);

  // Filter paired data
  const filterPairedData = (data) => {
    if (!searchQuery) return data;
    const query = searchQuery.toLowerCase();
    return data.filter(group =>
      group.boat?.toLowerCase().includes(query) ||
      group.date?.toLowerCase().includes(query) ||
      group.sbhSortie?.nomPax?.toLowerCase().includes(query) ||
      group.sbhEntree?.nomPax?.toLowerCase().includes(query) ||
      group.sxmSortie?.nomPax?.toLowerCase().includes(query) ||
      group.sxmEntree?.nomPax?.toLowerCase().includes(query)
    );
  };

  const renderCombinedTable = () => {
    const filteredEntrees = filterData(dataEntrees);
    const filteredSorties = filterData(dataSorties);
    const stats = getStats(filteredEntrees, filteredSorties);

    // Combine and sort by date
    const combined = [
      ...filteredEntrees.map(e => ({ ...e, type: 'entree' })),
      ...filteredSorties.map(s => ({ ...s, type: 'sortie' })),
    ].sort((a, b) => {
      const dateA = parseDate(a.date);
      const dateB = parseDate(b.date);
      if (!dateA && !dateB) return 0;
      if (!dateA) return 1;
      if (!dateB) return -1;
      return dateB.valueOf() - dateA.valueOf();
    });

    const paginatedData = paginateData(combined, currentPageEntrees);

    if (combined.length === 0) {
      return (
        <Empty
          description={searchQuery ? "Aucun résultat" : "Aucune clairance"}
          style={{ padding: spacing.xxl }}
        />
      );
    }

    return (
      <>
        {/* Stats */}
        <div style={styles.statsCard}>
          <div style={styles.statItem}>
            <Text type="secondary" style={{ fontSize: typography.fontSize.xs }}>TOTAL</Text>
            <div style={{ fontSize: typography.fontSize.xxl, fontWeight: typography.fontWeight.bold }}>
              {stats.total}
            </div>
          </div>
          <div style={{ ...styles.statItem, background: '#e6f7ff', borderColor: '#1890ff' }}>
            <Text type="secondary" style={{ fontSize: typography.fontSize.xs }}>📥 ENTRÉES</Text>
            <div style={{ fontSize: typography.fontSize.xl, fontWeight: typography.fontWeight.bold, color: '#1890ff' }}>
              {stats.totalEntrees}
            </div>
            {stats.pendingEntrees > 0 && (
              <Tag color="orange" style={{ marginTop: 4, fontSize: 10 }}>{stats.pendingEntrees} en attente</Tag>
            )}
          </div>
          <div style={{ ...styles.statItem, background: '#fff7e6', borderColor: '#fa8c16' }}>
            <Text type="secondary" style={{ fontSize: typography.fontSize.xs }}>📤 SORTIES</Text>
            <div style={{ fontSize: typography.fontSize.xl, fontWeight: typography.fontWeight.bold, color: '#fa8c16' }}>
              {stats.totalSorties}
            </div>
            {stats.pendingSorties > 0 && (
              <Tag color="orange" style={{ marginTop: 4, fontSize: 10 }}>{stats.pendingSorties} en attente</Tag>
            )}
          </div>
          {stats.expiring > 0 && (
            <div style={{ ...styles.statItem, borderColor: '#ff4d4f', background: 'rgba(255,77,79,0.05)' }}>
              <Text type="secondary" style={{ fontSize: typography.fontSize.xs }}>⚠️ EXPIRENT</Text>
              <div style={{ fontSize: typography.fontSize.xl, fontWeight: typography.fontWeight.bold, color: '#ff4d4f' }}>
                {stats.expiring}
              </div>
              <Text style={{ fontSize: 10, color: '#ff4d4f' }}>dans 48h</Text>
            </div>
          )}
        </div>

        {/* Workflow explanation */}
        <div style={styles.workflowCard}>
          <Text strong style={{ fontSize: typography.fontSize.sm, marginBottom: spacing.sm, display: 'block' }}>
            📋 Workflow des clairances
          </Text>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: spacing.lg }}>
            <div style={styles.workflowItem}>
              <Tag color="blue">Sortie SBH</Tag>
              <ArrowRightOutlined />
              <Tag color="green">Entrée SXM</Tag>
              <Text type="secondary" style={{ fontSize: 11 }}>puis</Text>
              <Tag color="orange">Sortie SXM</Tag>
              <ArrowRightOutlined />
              <Tag color="purple">Entrée SBH</Tag>
            </div>
            <div style={styles.workflowItem}>
              <SwapOutlined style={{ color: '#eb2f96' }} />
              <Text type="secondary" style={{ fontSize: 11 }}>Anguilla:</Text>
              <Tag color="blue">Sortie SBH</Tag>
              <ArrowRightOutlined />
              <Tag color="purple">Entrée SBH</Tag>
            </div>
          </div>
        </div>

        {/* Legend */}
        <div style={styles.legendContainer}>
          <div style={styles.legendItem}>
            <Tag color="blue">📥 Entrée</Tag>
          </div>
          <div style={styles.legendItem}>
            <Tag color="orange">📤 Sortie</Tag>
          </div>
          <div style={styles.legendItem}>
            <div style={{ width: 16, height: 16, background: 'rgba(255, 77, 79, 0.15)', borderRadius: 4, border: '1px solid rgba(255, 77, 79, 0.4)' }} />
            <span>Expire dans 48h</span>
          </div>
          <div style={styles.legendItem}>
            <div style={{ width: 16, height: 16, background: 'rgba(250, 173, 20, 0.15)', borderRadius: 4, border: '1px solid rgba(250, 173, 20, 0.4)' }} />
            <span>En attente</span>
          </div>
        </div>

        {/* Table */}
        <div style={styles.tableContainer}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Type</th>
                <th style={styles.th}>N° Clairance</th>
                <th style={styles.th}>Bateau</th>
                <th style={styles.th}>Date</th>
                <th style={styles.th}>Nom + Nb PAX</th>
                <th style={styles.th}>Heure</th>
                <th style={styles.th}>Statut</th>
              </tr>
            </thead>
            <tbody>
              {paginatedData.map((item, index) => {
                const pending = isPending(item.valide);
                const expiring = isExpiring(item.date) && pending;
                const isEntree = item.type === 'entree';

                const rowStyle = {
                  ...styles.td,
                  background: expiring ? 'rgba(255, 77, 79, 0.08)' : pending ? 'rgba(250, 173, 20, 0.08)' : 'transparent',
                };

                return (
                  <tr key={item.id || `${item.type}-${index}`}>
                    <td style={rowStyle}>
                      <Tag color={isEntree ? 'blue' : 'orange'} style={{ margin: 0 }}>
                        {isEntree ? '📥 Entrée' : '📤 Sortie'}
                      </Tag>
                    </td>
                    <td style={rowStyle}>
                      <Text strong style={{ fontFamily: 'monospace', fontSize: typography.fontSize.sm }}>
                        {item.numero || '-'}
                      </Text>
                    </td>
                    <td style={rowStyle}>
                      <Text strong>{item.bateau || '-'}</Text>
                    </td>
                    <td style={rowStyle}>
                      <Space size={4}>
                        {item.date || '-'}
                        {expiring && (
                          <Tooltip title="Expire dans moins de 48h !">
                            <WarningOutlined style={{ color: '#ff4d4f' }} />
                          </Tooltip>
                        )}
                      </Space>
                    </td>
                    <td style={rowStyle}>
                      <Text>{item.nomPax || '-'}</Text>
                    </td>
                    <td style={rowStyle}>
                      <Text>{item.heure || '-'}</Text>
                    </td>
                    <td style={rowStyle}>
                      {pending ? (
                        <Tag color="orange" icon={<ClockCircleOutlined />}>
                          En attente
                        </Tag>
                      ) : (
                        <Tag color="green" icon={<CheckCircleOutlined />}>
                          Validée
                        </Tag>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div style={styles.paginationContainer}>
          <Text type="secondary" style={{ fontSize: typography.fontSize.sm }}>
            Affichage {((currentPageEntrees - 1) * pageSize) + 1} - {Math.min(currentPageEntrees * pageSize, combined.length)} sur {combined.length}
          </Text>
          <Space>
            <Select
              value={pageSize}
              onChange={(value) => {
                setPageSize(value);
                setCurrentPageEntrees(1);
                setCurrentPageSorties(1);
              }}
              style={{ width: 100 }}
              size="small"
            >
              {PAGE_SIZE_OPTIONS.map(size => (
                <Select.Option key={size} value={size}>{size} / page</Select.Option>
              ))}
            </Select>
            <Pagination
              current={currentPageEntrees}
              total={combined.length}
              pageSize={pageSize}
              onChange={(page) => setCurrentPageEntrees(page)}
              showSizeChanger={false}
              size="small"
            />
          </Space>
        </div>
      </>
    );
  };

  const renderSeparateTable = (data, type, currentPage, setCurrentPage) => {
    const filteredData = filterData(data);
    const paginatedData = paginateData(filteredData, currentPage);

    const pendingCount = filteredData.filter(item => isPending(item.valide)).length;
    const expiringCount = filteredData.filter(item => isExpiring(item.date) && isPending(item.valide)).length;

    if (filteredData.length === 0) {
      return (
        <Empty
          description={searchQuery ? "Aucun résultat" : "Aucune clairance"}
          style={{ padding: spacing.xxl }}
        />
      );
    }

    return (
      <>
        {/* Quick stats */}
        <div style={{ display: 'flex', gap: spacing.md, marginBottom: spacing.md }}>
          <Tag color="default">Total: {filteredData.length}</Tag>
          {pendingCount > 0 && <Tag color="orange">{pendingCount} en attente</Tag>}
          {expiringCount > 0 && <Tag color="red">{expiringCount} expirent bientôt</Tag>}
        </div>

        {/* Table */}
        <div style={styles.tableContainer}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>N° Clairance</th>
                <th style={styles.th}>Bateau</th>
                <th style={styles.th}>Date</th>
                <th style={styles.th}>Nom + Nb PAX</th>
                <th style={styles.th}>Heure</th>
                <th style={styles.th}>Statut</th>
              </tr>
            </thead>
            <tbody>
              {paginatedData.map((item, index) => {
                const pending = isPending(item.valide);
                const expiring = isExpiring(item.date) && pending;

                const rowStyle = {
                  ...styles.td,
                  background: expiring ? 'rgba(255, 77, 79, 0.08)' : pending ? 'rgba(250, 173, 20, 0.08)' : 'transparent',
                };

                return (
                  <tr key={item.id || `${type}-${index}`}>
                    <td style={rowStyle}>
                      <Text strong style={{ fontFamily: 'monospace', fontSize: typography.fontSize.sm }}>
                        {item.numero || '-'}
                      </Text>
                    </td>
                    <td style={rowStyle}>
                      <Text strong>{item.bateau || '-'}</Text>
                    </td>
                    <td style={rowStyle}>
                      <Space size={4}>
                        {item.date || '-'}
                        {expiring && (
                          <Tooltip title="Expire dans moins de 48h !">
                            <WarningOutlined style={{ color: '#ff4d4f' }} />
                          </Tooltip>
                        )}
                      </Space>
                    </td>
                    <td style={rowStyle}>
                      <Text>{item.nomPax || '-'}</Text>
                    </td>
                    <td style={rowStyle}>
                      <Text>{item.heure || '-'}</Text>
                    </td>
                    <td style={rowStyle}>
                      {pending ? (
                        <Tag color="orange" icon={<ClockCircleOutlined />}>
                          En attente
                        </Tag>
                      ) : (
                        <Tag color="green" icon={<CheckCircleOutlined />}>
                          Validée
                        </Tag>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div style={styles.paginationContainer}>
          <Text type="secondary" style={{ fontSize: typography.fontSize.sm }}>
            Affichage {((currentPage - 1) * pageSize) + 1} - {Math.min(currentPage * pageSize, filteredData.length)} sur {filteredData.length}
          </Text>
          <Pagination
            current={currentPage}
            total={filteredData.length}
            pageSize={pageSize}
            onChange={(page) => setCurrentPage(page)}
            showSizeChanger={false}
            size="small"
          />
        </div>
      </>
    );
  };

  // Render a clearance cell
  const renderClearanceCell = (item, type, territory) => {
    if (!item) {
      return (
        <div style={{
          padding: spacing.sm,
          background: 'rgba(0,0,0,0.02)',
          borderRadius: radius.md,
          minHeight: 60,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: '1px dashed rgba(0,0,0,0.1)',
        }}>
          <Text type="secondary" style={{ fontSize: 11 }}>—</Text>
        </div>
      );
    }

    const pending = isPending(item.valide);
    const expiring = isExpiring(item.date) && pending;
    const isEntree = type === 'entree';

    const bgColor = expiring
      ? 'rgba(255, 77, 79, 0.1)'
      : pending
        ? 'rgba(250, 173, 20, 0.08)'
        : 'rgba(82, 196, 26, 0.08)';

    const borderColor = expiring
      ? 'rgba(255, 77, 79, 0.3)'
      : pending
        ? 'rgba(250, 173, 20, 0.3)'
        : 'rgba(82, 196, 26, 0.3)';

    return (
      <div style={{
        padding: spacing.sm,
        background: bgColor,
        borderRadius: radius.md,
        border: `1px solid ${borderColor}`,
        minHeight: 60,
        position: 'relative',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
          <Tag
            color={isEntree ? 'blue' : 'orange'}
            style={{ margin: 0, fontSize: 10, padding: '0 6px' }}
          >
            {isEntree ? '📥' : '📤'} {territory}
          </Tag>
          <Tooltip title={pending ? "Cliquer pour valider" : "Cliquer pour retirer"}>
            <Button
              type="text"
              size="small"
              style={{ padding: 0, height: 'auto' }}
              onClick={(e) => {
                e.stopPropagation();
                handleToggleValidation(item, type, territory);
              }}
            >
              {pending ? (
                <ClockCircleOutlined style={{ color: '#faad14', fontSize: 12 }} />
              ) : (
                <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 12 }} />
              )}
            </Button>
          </Tooltip>
        </div>
        <div style={{ fontSize: 11, color: colors.textSecondary }}>
          <div style={{ fontFamily: 'monospace', fontWeight: 600 }}>
            {item.numero || '-'}
          </div>
          {item.heure && (
            <div style={{ color: colors.textMuted }}>
              {item.heure}
            </div>
          )}
          {item.nomPax && (
            <div style={{ fontSize: 10, color: colors.textMuted, marginTop: 2 }}>
              {item.nomPax}
            </div>
          )}
        </div>
        {expiring && (
          <div style={{ marginTop: 4 }}>
            <Tag color="red" style={{ fontSize: 9, padding: '0 4px' }}>
              ⚠️ Expire 48h
            </Tag>
          </div>
        )}
        {/* Action buttons */}
        <div style={{
          display: 'flex',
          gap: 4,
          marginTop: spacing.xs,
          paddingTop: spacing.xs,
          borderTop: '1px dashed rgba(0,0,0,0.1)',
        }}>
          <Tooltip title="Modifier">
            <Button
              type="text"
              size="small"
              icon={<EditOutlined style={{ fontSize: 11 }} />}
              style={{ padding: '2px 4px', height: 'auto', fontSize: 10 }}
              onClick={(e) => {
                e.stopPropagation();
                handleEdit(item, type, territory);
              }}
            />
          </Tooltip>
          <Popconfirm
            title="Supprimer cette clairance ?"
            onConfirm={(e) => {
              e?.stopPropagation();
              handleDelete(item, type, territory);
            }}
            okText="Oui"
            cancelText="Non"
          >
            <Tooltip title="Supprimer">
              <Button
                type="text"
                size="small"
                danger
                icon={<DeleteOutlined style={{ fontSize: 11 }} />}
                style={{ padding: '2px 4px', height: 'auto', fontSize: 10 }}
                onClick={(e) => e.stopPropagation()}
              />
            </Tooltip>
          </Popconfirm>
        </div>
      </div>
    );
  };

  // Render paired view
  const renderPairedView = () => {
    const filteredData = filterPairedData(pairedClairances);
    const paginatedData = paginateData(filteredData, currentPagePaired);

    const totalSbhSorties = filteredData.filter(g => g.sbhSortie).length;
    const totalSbhEntrees = filteredData.filter(g => g.sbhEntree).length;
    const totalSxmSorties = filteredData.filter(g => g.sxmSortie).length;
    const totalSxmEntrees = filteredData.filter(g => g.sxmEntree).length;

    const pendingCount = filteredData.filter(g =>
      (g.sbhSortie && isPending(g.sbhSortie.valide)) ||
      (g.sbhEntree && isPending(g.sbhEntree.valide)) ||
      (g.sxmSortie && isPending(g.sxmSortie.valide)) ||
      (g.sxmEntree && isPending(g.sxmEntree.valide))
    ).length;

    if (filteredData.length === 0) {
      return (
        <Empty
          description={searchQuery ? "Aucun résultat" : "Aucune clairance"}
          style={{ padding: spacing.xxl }}
        />
      );
    }

    return (
      <>
        {/* Stats row */}
        <div style={styles.statsCard}>
          <div style={styles.statItem}>
            <Text type="secondary" style={{ fontSize: typography.fontSize.xs }}>TRAJETS</Text>
            <div style={{ fontSize: typography.fontSize.xxl, fontWeight: typography.fontWeight.bold }}>
              {filteredData.length}
            </div>
          </div>
          <div style={{ ...styles.statItem, background: '#f6ffed', borderColor: '#52c41a' }}>
            <Text type="secondary" style={{ fontSize: typography.fontSize.xs }}>🇧🇱 SBH</Text>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 4 }}>
              <Tag color="orange" style={{ fontSize: 10 }}>📤 {totalSbhSorties}</Tag>
              <Tag color="blue" style={{ fontSize: 10 }}>📥 {totalSbhEntrees}</Tag>
            </div>
          </div>
          <div style={{ ...styles.statItem, background: '#e6f7ff', borderColor: '#1890ff' }}>
            <Text type="secondary" style={{ fontSize: typography.fontSize.xs }}>🇸🇽 SXM</Text>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 4 }}>
              <Tag color="green" style={{ fontSize: 10 }}>📥 {totalSxmEntrees}</Tag>
              <Tag color="volcano" style={{ fontSize: 10 }}>📤 {totalSxmSorties}</Tag>
            </div>
          </div>
          {pendingCount > 0 && (
            <div style={{ ...styles.statItem, borderColor: '#faad14', background: 'rgba(250, 173, 20, 0.05)' }}>
              <Text type="secondary" style={{ fontSize: typography.fontSize.xs }}>⏳ EN ATTENTE</Text>
              <div style={{ fontSize: typography.fontSize.xl, fontWeight: typography.fontWeight.bold, color: '#faad14' }}>
                {pendingCount}
              </div>
            </div>
          )}
        </div>

        {/* Workflow explanation */}
        <div style={styles.workflowCard}>
          <Text strong style={{ fontSize: typography.fontSize.sm, marginBottom: spacing.sm, display: 'block' }}>
            📋 Workflow des clairances - Vue appairée
          </Text>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: spacing.lg }}>
            <div style={styles.workflowItem}>
              <Tag color="orange">Sortie SBH</Tag>
              <DoubleRightOutlined style={{ color: '#1890ff' }} />
              <Tag color="green">Entrée SXM</Tag>
              <Text type="secondary" style={{ fontSize: 11, margin: '0 4px' }}>→</Text>
              <Tag color="volcano">Sortie SXM</Tag>
              <DoubleRightOutlined style={{ color: '#52c41a' }} />
              <Tag color="blue">Entrée SBH</Tag>
            </div>
          </div>
        </div>

        {/* Paired cards */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.md }}>
          {paginatedData.map((group, index) => {
            // Determine if this is a complete round trip, going to SXM, coming back, or Anguilla
            const isGoingToSxm = group.sbhSortie && group.sxmEntree;
            const isComingBack = group.sxmSortie && group.sbhEntree;
            const isAnguilla = group.sbhSortie && group.sbhEntree && !group.sxmEntree && !group.sxmSortie;

            return (
              <Card
                key={`${group.boat}-${group.date}-${index}`}
                size="small"
                style={{
                  borderRadius: radius.lg,
                  border: `1px solid ${colors.borderLight}`,
                  background: colors.bgSecondary,
                }}
                bodyStyle={{ padding: spacing.md }}
              >
                {/* Header */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: spacing.md,
                  paddingBottom: spacing.sm,
                  borderBottom: `1px solid ${colors.borderLight}`,
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: spacing.sm }}>
                    <Text strong style={{ fontSize: typography.fontSize.md }}>
                      🚤 {group.boat}
                    </Text>
                    <Tag color="default">{group.date}</Tag>
                    {isAnguilla && (
                      <Tag color="magenta">🏝️ Anguilla</Tag>
                    )}
                  </div>
                  <div>
                    {isGoingToSxm && isComingBack && (
                      <Tag color="success">✅ Aller-retour complet</Tag>
                    )}
                    {isGoingToSxm && !isComingBack && (
                      <Tag color="processing">🔄 En cours (aller)</Tag>
                    )}
                    {!isGoingToSxm && isComingBack && (
                      <Tag color="processing">🔄 Retour uniquement</Tag>
                    )}
                  </div>
                </div>

                {/* 4-column grid */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(4, 1fr)',
                  gap: spacing.sm,
                }}>
                  {/* SBH Sortie */}
                  <div>
                    <Text type="secondary" style={{ fontSize: 10, textTransform: 'uppercase', display: 'block', marginBottom: 4 }}>
                      1️⃣ Sortie SBH
                    </Text>
                    {renderClearanceCell(group.sbhSortie, 'sortie', 'SBH')}
                  </div>

                  {/* SXM Entrée */}
                  <div>
                    <Text type="secondary" style={{ fontSize: 10, textTransform: 'uppercase', display: 'block', marginBottom: 4 }}>
                      2️⃣ Entrée SXM
                    </Text>
                    {renderClearanceCell(group.sxmEntree, 'entree', 'SXM')}
                  </div>

                  {/* SXM Sortie */}
                  <div>
                    <Text type="secondary" style={{ fontSize: 10, textTransform: 'uppercase', display: 'block', marginBottom: 4 }}>
                      3️⃣ Sortie SXM
                    </Text>
                    {renderClearanceCell(group.sxmSortie, 'sortie', 'SXM')}
                  </div>

                  {/* SBH Entrée */}
                  <div>
                    <Text type="secondary" style={{ fontSize: 10, textTransform: 'uppercase', display: 'block', marginBottom: 4 }}>
                      4️⃣ Entrée SBH
                    </Text>
                    {renderClearanceCell(group.sbhEntree, 'entree', 'SBH')}
                  </div>
                </div>

                {/* Arrow indicators */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(4, 1fr)',
                  marginTop: spacing.xs,
                }}>
                  <div style={{ textAlign: 'center' }}>
                    {group.sbhSortie && group.sxmEntree && (
                      <ArrowRightOutlined style={{ color: '#1890ff' }} />
                    )}
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    {group.sxmEntree && group.sxmSortie && (
                      <ArrowRightOutlined style={{ color: '#722ed1' }} />
                    )}
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    {group.sxmSortie && group.sbhEntree && (
                      <ArrowRightOutlined style={{ color: '#52c41a' }} />
                    )}
                  </div>
                  <div></div>
                </div>
              </Card>
            );
          })}
        </div>

        {/* Pagination */}
        <div style={styles.paginationContainer}>
          <Text type="secondary" style={{ fontSize: typography.fontSize.sm }}>
            Affichage {((currentPagePaired - 1) * pageSize) + 1} - {Math.min(currentPagePaired * pageSize, filteredData.length)} sur {filteredData.length} trajets
          </Text>
          <Space>
            <Select
              value={pageSize}
              onChange={(value) => {
                setPageSize(value);
                setCurrentPagePaired(1);
              }}
              style={{ width: 100 }}
              size="small"
            >
              {PAGE_SIZE_OPTIONS.map(size => (
                <Select.Option key={size} value={size}>{size} / page</Select.Option>
              ))}
            </Select>
            <Pagination
              current={currentPagePaired}
              total={filteredData.length}
              pageSize={pageSize}
              onChange={(page) => setCurrentPagePaired(page)}
              showSizeChanger={false}
              size="small"
            />
          </Space>
        </div>
      </>
    );
  };

  if (!isAuthenticated) {
    return (
      <div style={styles.page}>
        <div style={styles.container}>
          <div style={styles.card}>
            <div style={{ textAlign: 'center', padding: spacing.xxl }}>
              <GoogleOutlined style={{ fontSize: 48, color: '#4285f4', marginBottom: spacing.lg }} />
              <Title level={3}>Connexion Google requise</Title>
              <Text type="secondary" style={{ display: 'block', marginBottom: spacing.xl }}>
                Connectez-vous à Google pour accéder aux clairances depuis Google Sheets
              </Text>
              <Button
                type="primary"
                size="large"
                icon={<GoogleOutlined />}
                onClick={handleAuth}
                style={{ background: '#4285f4', borderColor: '#4285f4' }}
              >
                Se connecter avec Google
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.7; }
        }
      `}</style>

      <div style={styles.container}>
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.headerLeft}>
            <div style={styles.headerIcon}>🚢</div>
            <div>
              <Title level={2} style={{ margin: 0, color: colors.textPrimary }}>
                Suivi des Clairances
              </Title>
              <Text style={{ color: colors.textMuted }}>
                Entrées et sorties - {lastRefresh ? `Mis à jour ${lastRefresh.format('HH:mm')}` : 'Non chargé'}
              </Text>
            </div>
          </div>
          <Space>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleOpenAdd}
            >
              Ajouter
            </Button>
            <Select
              value={viewMode}
              onChange={(value) => {
                setViewMode(value);
                setCurrentPagePaired(1);
                setCurrentPageEntrees(1);
                setCurrentPageSorties(1);
              }}
              style={{ width: 160 }}
              size="small"
            >
              <Select.Option value="paired">🔗 Vue appairée</Select.Option>
              <Select.Option value="combined">📋 Vue combinée</Select.Option>
              <Select.Option value="separate">📑 Vue séparée</Select.Option>
            </Select>
            <Button
              icon={<ExportOutlined />}
              onClick={() => window.open(`https://docs.google.com/spreadsheets/d/${CLAIRANCES_SHEET_ID}`, '_blank')}
            >
              Google Sheets
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={fetchData}
              loading={loading}
            >
              Actualiser
            </Button>
          </Space>
        </div>

        {error && (
          <Alert
            message="Erreur"
            description={error}
            type="error"
            showIcon
            closable
            style={{ marginBottom: spacing.lg, borderRadius: radius.lg }}
          />
        )}

        {/* Search & Territory Tabs */}
        <div style={styles.card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: spacing.lg }}>
            <Input
              placeholder="Rechercher par numéro, bateau, nom..."
              prefix={<SearchOutlined style={{ color: colors.textMuted }} />}
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPageEntrees(1);
                setCurrentPageSorties(1);
              }}
              allowClear
              style={{ maxWidth: 400, borderRadius: radius.md }}
            />
            <Space>
              <Badge count={dataEntrees.filter(d => isPending(d.valide)).length} style={{ background: '#1890ff' }}>
                <Tag color="blue">📥 Entrées</Tag>
              </Badge>
              <Badge count={dataSorties.filter(d => isPending(d.valide)).length} style={{ background: '#fa8c16' }}>
                <Tag color="orange">📤 Sorties</Tag>
              </Badge>
            </Space>
          </div>

          {/* Territory tabs (SXM / SBH) - only show for non-paired views */}
          {viewMode !== 'paired' && sheetNames.length > 0 && (
            <Tabs
              activeKey={activeTab}
              onChange={(key) => {
                setActiveTab(key);
                setCurrentPageEntrees(1);
                setCurrentPageSorties(1);
              }}
              items={sheetNames.map(name => ({
                key: name,
                label: <span>{name.includes('SXM') ? '🇸🇽' : name.includes('SBH') ? '🇧🇱' : '📋'} {name}</span>,
              }))}
            />
          )}

          {/* Info banner for paired view */}
          {viewMode === 'paired' && (
            <Alert
              message="Vue appairée SBH ↔ SXM"
              description="Cette vue montre les clairances correspondantes entre Saint-Barthélemy et Sint Maarten groupées par bateau et date."
              type="info"
              showIcon
              style={{ marginBottom: spacing.md, borderRadius: radius.md }}
            />
          )}

          {loading ? (
            <div style={{ textAlign: 'center', padding: spacing.xxl }}>
              <Spin size="large" />
              <div style={{ marginTop: spacing.md, color: colors.textMuted }}>
                Chargement des clairances...
              </div>
            </div>
          ) : viewMode === 'paired' ? (
            renderPairedView()
          ) : viewMode === 'combined' ? (
            renderCombinedTable()
          ) : (
            <Tabs
              defaultActiveKey="entrees"
              items={[
                {
                  key: 'entrees',
                  label: (
                    <Space>
                      <span>📥 Entrées</span>
                      <Badge count={dataEntrees.filter(d => isPending(d.valide)).length} style={{ background: '#faad14' }} />
                    </Space>
                  ),
                  children: renderSeparateTable(dataEntrees, 'entrees', currentPageEntrees, setCurrentPageEntrees),
                },
                {
                  key: 'sorties',
                  label: (
                    <Space>
                      <span>📤 Sorties</span>
                      <Badge count={dataSorties.filter(d => isPending(d.valide)).length} style={{ background: '#faad14' }} />
                    </Space>
                  ),
                  children: renderSeparateTable(dataSorties, 'sorties', currentPageSorties, setCurrentPageSorties),
                },
              ]}
            />
          )}
        </div>

        {/* Quick info */}
        <Alert
          message="Rappel"
          description={
            <div>
              <div>Les clairances expirent après 48h. Vérifiez régulièrement les clairances en attente.</div>
              <div style={{ marginTop: 8, fontSize: 12, color: '#666' }}>
                <strong>Flux normal:</strong> Sortie SBH → Entrée SXM, puis Sortie SXM → Entrée SBH<br/>
                <strong>Anguilla:</strong> Sortie SBH → Entrée SBH (le bateau va à Anguilla et revient)
              </div>
            </div>
          }
          type="info"
          showIcon
          style={{ borderRadius: radius.lg }}
        />
      </div>

      {/* Edit Drawer */}
      <Drawer
        title={
          <Space>
            <EditOutlined />
            Modifier la clairance
            {editingItem && (
              <Tag color={editingItem.type === 'entree' ? 'blue' : 'orange'}>
                {editingItem.type === 'entree' ? '📥 Entrée' : '📤 Sortie'} {editingItem.territory}
              </Tag>
            )}
          </Space>
        }
        open={editDrawerVisible}
        onClose={() => {
          setEditDrawerVisible(false);
          setEditingItem(null);
          editForm.resetFields();
        }}
        width={400}
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button onClick={() => setEditDrawerVisible(false)}>
              Annuler
            </Button>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              loading={saving}
              onClick={handleSaveEdit}
            >
              Enregistrer
            </Button>
          </div>
        }
      >
        <Form
          form={editForm}
          layout="vertical"
        >
          <Form.Item
            name="numero"
            label="N° Clairance"
          >
            <Input placeholder="Ex: CLR-2024-001" />
          </Form.Item>

          <Form.Item
            name="bateau"
            label="Bateau"
            rules={[{ required: true, message: 'Requis' }]}
          >
            <Input placeholder="Nom du bateau" />
          </Form.Item>

          <Form.Item
            name="nomPax"
            label="Nom + Nb PAX"
          >
            <Input placeholder="Ex: DUPONT (4 pax)" />
          </Form.Item>

          <Form.Item
            name="date"
            label="Date"
            rules={[{ required: true, message: 'Requis' }]}
          >
            <Input placeholder="DD/MM/YYYY" />
          </Form.Item>

          <Form.Item
            name="heure"
            label="Heure"
          >
            <Input placeholder="Ex: 10:00" />
          </Form.Item>

          <Form.Item
            name="valide"
            valuePropName="checked"
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input
                type="checkbox"
                id="valide-checkbox"
                style={{ width: 18, height: 18 }}
                checked={editForm.getFieldValue('valide')}
                onChange={(e) => editForm.setFieldValue('valide', e.target.checked)}
              />
              <label htmlFor="valide-checkbox" style={{ cursor: 'pointer' }}>
                Clairance validée ✔️
              </label>
            </div>
          </Form.Item>
        </Form>
      </Drawer>

      {/* Add Modal */}
      <Modal
        title={
          <Space>
            <PlusOutlined />
            Nouvelle clairance
          </Space>
        }
        open={addModalVisible}
        onCancel={() => {
          setAddModalVisible(false);
          addForm.resetFields();
        }}
        footer={[
          <Button key="cancel" onClick={() => setAddModalVisible(false)}>
            Annuler
          </Button>,
          <Button
            key="submit"
            type="primary"
            icon={<SaveOutlined />}
            loading={saving}
            onClick={handleAdd}
          >
            Ajouter
          </Button>,
        ]}
        width={500}
      >
        <div style={{ marginBottom: spacing.lg }}>
          <Space size="large">
            <div>
              <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>
                Territoire
              </Text>
              <Select
                value={addTerritory}
                onChange={setAddTerritory}
                style={{ width: 120 }}
              >
                <Select.Option value="SBH">🇧🇱 SBH</Select.Option>
                <Select.Option value="SXM">🇸🇽 SXM</Select.Option>
              </Select>
            </div>
            <div>
              <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>
                Type
              </Text>
              <Select
                value={addType}
                onChange={setAddType}
                style={{ width: 140 }}
              >
                <Select.Option value="entree">📥 Entrée</Select.Option>
                <Select.Option value="sortie">📤 Sortie</Select.Option>
              </Select>
            </div>
          </Space>
        </div>

        <Form
          form={addForm}
          layout="vertical"
        >
          <Form.Item
            name="numero"
            label="N° Clairance"
          >
            <Input placeholder="Ex: CLR-2024-001" />
          </Form.Item>

          <Form.Item
            name="bateau"
            label="Bateau"
            rules={[{ required: true, message: 'Requis' }]}
          >
            <Input placeholder="Nom du bateau" />
          </Form.Item>

          <Form.Item
            name="nomPax"
            label="Nom + Nb PAX"
          >
            <Input placeholder="Ex: DUPONT (4 pax)" />
          </Form.Item>

          <Form.Item
            name="date"
            label="Date"
            rules={[{ required: true, message: 'Requis' }]}
          >
            <Input placeholder="DD/MM/YYYY" />
          </Form.Item>

          <Form.Item
            name="heure"
            label="Heure"
          >
            <Input placeholder="Ex: 10:00" />
          </Form.Item>

          <Form.Item
            name="valide"
            valuePropName="checked"
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input
                type="checkbox"
                id="add-valide-checkbox"
                style={{ width: 18, height: 18 }}
              />
              <label htmlFor="add-valide-checkbox" style={{ cursor: 'pointer' }}>
                Clairance déjà validée ✔️
              </label>
            </div>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default ClairanceTracker;
